/**
 * @fileoverview Endless Runner Game Canvas
 * @description The main game component with all game logic
 */

import React, { memo, useEffect, useRef, useState, useCallback } from "react";
import {
  View,
  Text,
  TouchableWithoutFeedback,
  TouchableOpacity,
  StyleSheet,
  Dimensions,
  Animated,
} from "react-native";
import { Image } from "expo-image";
import { Ionicons } from "@expo/vector-icons";
import { useThemeColors } from "@/providers/ThemeProvider";
import {
  RunnerDifficulty,
  RUNNER_DIFFICULTY_CONFIGS,
  Obstacle,
  PlayerState,
  CharacterId,
  CHARACTER_CONFIGS,
  CharacterAbilitySettings,
  DEFAULT_CHARACTER_ABILITY_SETTINGS,
} from "../types";

// Character images
const OPA_IMAGE = require("@/assets/games/opa_endless_runner.svg");
const OMA_IMAGE = require("@/assets/games/omam_endless_runner.svg");

const { width: SCREEN_WIDTH } = Dimensions.get("window");
const GAME_HEIGHT = 200;
const GROUND_HEIGHT = 40;
const PLAYER_WIDTH = 50;
const PLAYER_HEIGHT = 60;
const PLAYER_X = 60;

interface GameCanvasProps {
  difficulty: RunnerDifficulty;
  characterId: CharacterId;
  abilitySettings?: CharacterAbilitySettings;
  isPlaying: boolean;
  isPaused: boolean;
  onScoreUpdate: (score: number) => void;
  onObstacleAvoided: () => void;
  onGameOver: (finalScore: number, obstaclesAvoided: number, livesRemaining: number, specialJumpsUsed: number) => void;
  onJump: () => void;
  onLiveLost?: (livesRemaining: number) => void;
  onSpecialJumpUsed?: (jumpsUsed: number, jumpsRemaining: number) => void;
}

/**
 * Generate unique ID
 */
const generateId = () => Math.random().toString(36).substring(7);

/**
 * GameCanvas Component
 */
export const GameCanvas = memo<GameCanvasProps>(
  ({
    difficulty,
    characterId,
    abilitySettings = DEFAULT_CHARACTER_ABILITY_SETTINGS,
    isPlaying,
    isPaused,
    onScoreUpdate,
    onObstacleAvoided,
    onGameOver,
    onJump,
    onLiveLost,
    onSpecialJumpUsed,
  }) => {
    const colors = useThemeColors();
    const characterConfig = CHARACTER_CONFIGS[characterId];
    const config = RUNNER_DIFFICULTY_CONFIGS[difficulty];

    // Animation values
    const playerY = useRef(new Animated.Value(0)).current;
    const groundOffset = useRef(new Animated.Value(0)).current;

    // Character ability values
    const maxLives = characterId === "oma" 
      ? abilitySettings.oma.lives 
      : 1;
    const maxSpecialJumps = characterId === "opa" 
      ? abilitySettings.opa.maxSpecialJumps 
      : 0;
    const jumpMultiplier = characterId === "opa" 
      ? abilitySettings.opa.jumpForceMultiplier 
      : 1.0;
    const invincibilityDuration = characterId === "oma" 
      ? abilitySettings.oma.invincibilityDuration 
      : 0;

    // Game state
    const [player, setPlayer] = useState<PlayerState>({
      y: 0,
      velocityY: 0,
      isJumping: false,
      isDucking: false,
    });
    const [obstacles, setObstacles] = useState<Obstacle[]>([]);
    const [score, setScore] = useState(0);
    const [obstaclesAvoided, setObstaclesAvoided] = useState(0);
    const [gameSpeed, setGameSpeed] = useState(config.initialSpeed);
    const [isGameOver, setIsGameOver] = useState(false);

    // Character state
    const [lives, setLives] = useState(maxLives);
    const [specialJumpsUsed, setSpecialJumpsUsed] = useState(0);
    const [isInvincible, setIsInvincible] = useState(false);
    const [showHitFlash, setShowHitFlash] = useState(false);

    // Refs for game loop
    const gameLoopRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const obstacleSpawnRef = useRef<ReturnType<typeof setInterval> | null>(null);
    const playerRef = useRef(player);
    const obstaclesRef = useRef(obstacles);
    const gameSpeedRef = useRef(gameSpeed);
    const scoreRef = useRef(score);
    const obstaclesAvoidedRef = useRef(obstaclesAvoided);
    const isGameOverRef = useRef(isGameOver);
    const hasCalledGameOverRef = useRef(false);
    const livesRef = useRef(lives);
    const specialJumpsUsedRef = useRef(specialJumpsUsed);
    const isInvincibleRef = useRef(isInvincible);

    // Update refs
    useEffect(() => {
      playerRef.current = player;
    }, [player]);

    useEffect(() => {
      obstaclesRef.current = obstacles;
    }, [obstacles]);

    useEffect(() => {
      gameSpeedRef.current = gameSpeed;
    }, [gameSpeed]);

    useEffect(() => {
      scoreRef.current = score;
      // Safely notify parent of score updates outside of render cycle
      if (score > 0) {
        onScoreUpdate(score);
      }
    }, [score, onScoreUpdate]);

    useEffect(() => {
      const prevValue = obstaclesAvoidedRef.current;
      obstaclesAvoidedRef.current = obstaclesAvoided;
      // Safely notify parent of obstacle avoided outside of render cycle
      if (obstaclesAvoided > prevValue) {
        onObstacleAvoided();
      }
    }, [obstaclesAvoided, onObstacleAvoided]);

    useEffect(() => {
      isGameOverRef.current = isGameOver;
      // Safely call onGameOver outside of render cycle
      if (isGameOver && !hasCalledGameOverRef.current) {
        hasCalledGameOverRef.current = true;
        onGameOver(
          scoreRef.current, 
          obstaclesAvoidedRef.current,
          livesRef.current,
          specialJumpsUsedRef.current
        );
      }
    }, [isGameOver, onGameOver]);

    useEffect(() => {
      livesRef.current = lives;
    }, [lives]);

    useEffect(() => {
      specialJumpsUsedRef.current = specialJumpsUsed;
    }, [specialJumpsUsed]);

    useEffect(() => {
      isInvincibleRef.current = isInvincible;
    }, [isInvincible]);

    /**
     * Handle jump
     */
    /**
     * Perform jump with specified force
     */
    const performJump = useCallback((jumpForce: number, jumpHeight: number, duration: number) => {
      setPlayer((prev) => ({
        ...prev,
        isJumping: true,
        velocityY: jumpForce,
      }));

      Animated.sequence([
        Animated.timing(playerY, {
          toValue: jumpHeight,
          duration,
          useNativeDriver: true,
        }),
        Animated.timing(playerY, {
          toValue: 0,
          duration,
          useNativeDriver: true,
        }),
      ]).start();
    }, [playerY]);

    /**
     * Handle normal jump (tap anywhere on screen)
     */
    const handleJump = useCallback(() => {
      if (!isPlaying || isPaused || isGameOver) return;
      if (playerRef.current.isJumping) return;

      onJump();
      performJump(config.jumpForce, -100, 300);
    }, [isPlaying, isPaused, isGameOver, config.jumpForce, onJump, performJump]);

    /**
     * Handle special jump (Opa only - triggered by button)
     */
    const handleSpecialJump = useCallback(() => {
      if (!isPlaying || isPaused || isGameOver) return;
      if (playerRef.current.isJumping) return;
      if (characterId !== "opa") return;
      if (specialJumpsUsedRef.current >= maxSpecialJumps) return;

      onJump();

      // Use special high jump
      const specialJumpForce = config.jumpForce * jumpMultiplier;
      setSpecialJumpsUsed((prev) => {
        const newValue = prev + 1;
        if (onSpecialJumpUsed) {
          setTimeout(() => onSpecialJumpUsed(newValue, maxSpecialJumps - newValue), 0);
        }
        return newValue;
      });

      performJump(specialJumpForce, -130, 350);
    }, [isPlaying, isPaused, isGameOver, config.jumpForce, onJump, characterId, maxSpecialJumps, jumpMultiplier, onSpecialJumpUsed, performJump]);

    /**
     * Check if special jump is available
     */
    const canSpecialJump = characterId === "opa" && specialJumpsUsed < maxSpecialJumps && !player.isJumping;

    /**
     * Spawn obstacle
     */
    const spawnObstacle = useCallback(() => {
      const types: Obstacle["type"][] = ["cactus", "rock", "cactus"];
      const type = types[Math.floor(Math.random() * types.length)];

      const obstacle: Obstacle = {
        id: generateId(),
        x: SCREEN_WIDTH,
        height: type === "bird" ? 30 : 40 + Math.random() * 20,
        width: type === "rock" ? 35 : 25,
        type,
        passed: false,
      };

      setObstacles((prev) => [...prev, obstacle]);
    }, []);

    /**
     * Check collision
     */
    const checkCollision = useCallback(
      (playerState: PlayerState, obstacle: Obstacle): boolean => {
        const playerLeft = PLAYER_X;
        const playerRight = PLAYER_X + PLAYER_WIDTH;
        const playerBottom = GROUND_HEIGHT + playerState.y;
        const playerTop = playerBottom + PLAYER_HEIGHT;

        const obstacleLeft = obstacle.x;
        const obstacleRight = obstacle.x + obstacle.width;
        const obstacleBottom = GROUND_HEIGHT;
        const obstacleTop = GROUND_HEIGHT + obstacle.height;

        // Check if player overlaps with obstacle
        const horizontalOverlap =
          playerRight > obstacleLeft && playerLeft < obstacleRight;
        const verticalOverlap =
          playerBottom < obstacleTop && playerTop > obstacleBottom;

        return horizontalOverlap && verticalOverlap;
      },
      []
    );

    /**
     * Game loop
     */
    useEffect(() => {
      if (!isPlaying || isPaused || isGameOver) {
        if (gameLoopRef.current) {
          clearInterval(gameLoopRef.current);
          gameLoopRef.current = null;
        }
        if (obstacleSpawnRef.current) {
          clearInterval(obstacleSpawnRef.current);
          obstacleSpawnRef.current = null;
        }
        return;
      }

      // Game loop - runs every 16ms (~60fps)
      gameLoopRef.current = setInterval(() => {
        // Update player physics
        setPlayer((prev) => {
          let newY = prev.y + prev.velocityY;
          let newVelocityY = prev.velocityY - config.gravity;
          let newIsJumping = prev.isJumping;

          // Ground check
          if (newY <= 0) {
            newY = 0;
            newVelocityY = 0;
            newIsJumping = false;
          }

          return {
            ...prev,
            y: newY,
            velocityY: newVelocityY,
            isJumping: newIsJumping,
          };
        });

        // Update obstacles - avoid calling callbacks inside setState
        setObstacles((prev) => {
          if (isGameOverRef.current) return prev;

          const updated = prev
            .map((obs) => ({
              ...obs,
              x: obs.x - gameSpeedRef.current,
            }))
            .filter((obs) => obs.x > -50);

          // Track passed obstacles count for later
          let newlyPassed = 0;
          updated.forEach((obs) => {
            if (!obs.passed && obs.x + obs.width < PLAYER_X) {
              obs.passed = true;
              newlyPassed++;
            }
          });

          // Update obstacles avoided count outside of this callback
          if (newlyPassed > 0) {
            // Use setTimeout to escape the setState callback
            setTimeout(() => {
              setObstaclesAvoided((o) => o + newlyPassed);
            }, 0);
          }

          // Check collisions (skip if invincible)
          if (!isInvincibleRef.current) {
            for (const obs of updated) {
              if (checkCollision(playerRef.current, obs)) {
                // IMMEDIATELY mark as invincible to prevent double-hit on next frame
                isInvincibleRef.current = true;
                
                // Use setTimeout to escape the setState callback
                setTimeout(() => {
                  // Oma has multiple lives
                  if (characterId === "oma" && livesRef.current > 1) {
                    // Lose a life but continue
                    setLives((prev) => {
                      const newLives = prev - 1;
                      livesRef.current = newLives; // Update ref immediately too
                      if (onLiveLost) {
                        setTimeout(() => onLiveLost(newLives), 0);
                      }
                      return newLives;
                    });
                    // Start invincibility state
                    setIsInvincible(true);
                    setShowHitFlash(true);
                    setTimeout(() => {
                      setIsInvincible(false);
                      setShowHitFlash(false);
                      isInvincibleRef.current = false; // Reset ref when invincibility ends
                    }, invincibilityDuration);
                  } else {
                    // Game over - reset invincible ref since game is ending
                    isInvincibleRef.current = false;
                    setIsGameOver(true);
                  }
                }, 0);
                return updated;
              }
            }
          }

          return updated;
        });

        // Update score
        setScore((prev) => prev + 1);

        // Increase speed
        setGameSpeed((prev) =>
          Math.min(prev + config.speedIncreaseRate, config.maxSpeed)
        );
      }, 16);

      // Obstacle spawner
      obstacleSpawnRef.current = setInterval(() => {
        spawnObstacle();
      }, config.obstacleInterval);

      return () => {
        if (gameLoopRef.current) {
          clearInterval(gameLoopRef.current);
        }
        if (obstacleSpawnRef.current) {
          clearInterval(obstacleSpawnRef.current);
        }
      };
    }, [
      isPlaying,
      isPaused,
      isGameOver,
      config,
      spawnObstacle,
      checkCollision,
      characterId,
      invincibilityDuration,
      onLiveLost,
    ]);

    /**
     * Ground animation
     */
    useEffect(() => {
      if (!isPlaying || isPaused || isGameOver) return;

      const animation = Animated.loop(
        Animated.timing(groundOffset, {
          toValue: -50,
          duration: 500,
          useNativeDriver: true,
        })
      );
      animation.start();

      return () => animation.stop();
    }, [isPlaying, isPaused, isGameOver, groundOffset]);

    /**
     * Reset game
     */
    useEffect(() => {
      if (isPlaying && !isPaused) {
        setScore(0);
        setObstacles([]);
        setObstaclesAvoided(0);
        setGameSpeed(config.initialSpeed);
        setIsGameOver(false);
        hasCalledGameOverRef.current = false;
        setPlayer({
          y: 0,
          velocityY: 0,
          isJumping: false,
          isDucking: false,
        });
        playerY.setValue(0);
        // Reset character state
        setLives(maxLives);
        setSpecialJumpsUsed(0);
        setIsInvincible(false);
        setShowHitFlash(false);
      }
    }, [isPlaying, config.initialSpeed, playerY, maxLives]);

    return (
      <TouchableWithoutFeedback onPress={handleJump}>
        <View style={[styles.container, { backgroundColor: colors.background }]}>
          {/* Sky */}
          <View style={[styles.sky, { backgroundColor: "#87CEEB" }]}>
            {/* Clouds */}
            <View style={[styles.cloud, { left: 50, top: 20 }]} />
            <View style={[styles.cloud, { left: 150, top: 40 }]} />
            <View style={[styles.cloud, { left: 280, top: 15 }]} />
          </View>

          {/* Game Area */}
          <View style={styles.gameArea}>
            {/* Player */}
            <Animated.View
              style={[
                styles.player,
                {
                  left: PLAYER_X,
                  transform: [{ translateY: playerY }],
                  opacity: showHitFlash ? 0.5 : 1,
                },
              ]}
            >
              <Image
                source={characterId === "opa" ? OPA_IMAGE : OMA_IMAGE}
                style={styles.characterImage}
                resizeMode="contain"
              />
            </Animated.View>

            {/* Obstacles */}
            {obstacles.map((obstacle) => (
              <View
                key={obstacle.id}
                style={[
                  styles.obstacle,
                  {
                    left: obstacle.x,
                    width: obstacle.width,
                    height: obstacle.height,
                    backgroundColor:
                      obstacle.type === "cactus"
                        ? "#22C55E"
                        : obstacle.type === "rock"
                        ? "#6B7280"
                        : "#8B5CF6",
                  },
                ]}
              >
                {obstacle.type === "cactus" && (
                  <>
                    <View style={styles.cactusArm} />
                    <View style={[styles.cactusArm, styles.cactusArmRight]} />
                  </>
                )}
              </View>
            ))}
          </View>

          {/* Ground */}
          <View style={[styles.ground, { backgroundColor: "#92400E" }]}>
            <Animated.View
              style={[
                styles.groundPattern,
                { transform: [{ translateX: groundOffset }] },
              ]}
            >
              {Array.from({ length: 20 }).map((_, i) => (
                <View key={i} style={styles.groundLine} />
              ))}
            </Animated.View>
          </View>

          {/* Score Display */}
          <View style={styles.scoreDisplay}>
            <Text style={styles.scoreText}>{score}</Text>
          </View>

          {/* Character Ability UI */}
          <View style={styles.abilityDisplay}>
            {characterId === "oma" && (
              // Lives display for Oma
              <View style={styles.livesContainer}>
                {Array.from({ length: maxLives }).map((_, i) => (
                  <Ionicons
                    key={i}
                    name={i < lives ? "heart" : "heart-outline"}
                    size={18}
                    color={i < lives ? "#EF4444" : "#6B7280"}
                  />
                ))}
              </View>
            )}
            {characterId === "opa" && (
              // Special jumps display for Opa
              <View style={styles.jumpsContainer}>
                <Ionicons name="arrow-up-circle" size={16} color="#4F46E5" />
                <Text style={styles.jumpsText}>
                  {maxSpecialJumps - specialJumpsUsed}/{maxSpecialJumps}
                </Text>
              </View>
            )}
          </View>

          {/* Hit flash overlay */}
          {showHitFlash && (
            <View style={styles.hitFlashOverlay} />
          )}

          {/* Pause Overlay */}
          {isPaused && (
            <View style={styles.pauseOverlay}>
              <Ionicons name="pause-circle" size={64} color="#FFFFFF" />
              <Text style={styles.pauseText}>Gepauzeerd</Text>
            </View>
          )}

          {/* Tap instruction */}
          {!isGameOver && (
            <View style={styles.tapInstruction}>
              <Text style={styles.tapText}>Tik om te springen!</Text>
            </View>
          )}

          {/* Special Jump Button for Opa */}
          {characterId === "opa" && !isGameOver && !isPaused && (
            <TouchableOpacity
              onPress={handleSpecialJump}
              disabled={!canSpecialJump}
              style={[
                styles.specialJumpButton,
                {
                  opacity: canSpecialJump ? 1 : 0.4,
                  backgroundColor: canSpecialJump ? "#4F46E5" : "#6B7280",
                },
              ]}
              activeOpacity={0.7}
              accessibilityRole="button"
              accessibilityLabel="Speciale sprong"
              accessibilityHint="Gebruik een speciale hoge sprong"
            >
              <Ionicons name="arrow-up" size={24} color="#FFFFFF" />
              <Text style={styles.specialJumpButtonText}>
                {maxSpecialJumps - specialJumpsUsed}
              </Text>
            </TouchableOpacity>
          )}
        </View>
      </TouchableWithoutFeedback>
    );
  }
);

GameCanvas.displayName = "GameCanvas";

const styles = StyleSheet.create({
  container: {
    height: GAME_HEIGHT + GROUND_HEIGHT,
    borderRadius: 16,
    overflow: "hidden",
  },
  sky: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: GAME_HEIGHT,
  },
  cloud: {
    position: "absolute",
    width: 60,
    height: 25,
    backgroundColor: "#FFFFFF",
    borderRadius: 20,
    opacity: 0.8,
  },
  gameArea: {
    position: "absolute",
    bottom: GROUND_HEIGHT,
    left: 0,
    right: 0,
    height: GAME_HEIGHT - GROUND_HEIGHT,
  },
  player: {
    position: "absolute",
    bottom: 0,
    width: PLAYER_WIDTH,
    height: PLAYER_HEIGHT,
  },
  characterImage: {
    width: PLAYER_WIDTH,
    height: PLAYER_HEIGHT,
  },
  obstacle: {
    position: "absolute",
    bottom: 0,
    borderTopLeftRadius: 4,
    borderTopRightRadius: 4,
  },
  cactusArm: {
    position: "absolute",
    width: 10,
    height: 15,
    backgroundColor: "#22C55E",
    left: -8,
    top: 10,
    borderRadius: 3,
  },
  cactusArmRight: {
    left: undefined,
    right: -8,
  },
  ground: {
    position: "absolute",
    bottom: 0,
    left: 0,
    right: 0,
    height: GROUND_HEIGHT,
    overflow: "hidden",
  },
  groundPattern: {
    flexDirection: "row",
    position: "absolute",
    bottom: 0,
    left: 0,
  },
  groundLine: {
    width: 2,
    height: 8,
    backgroundColor: "#78350F",
    marginHorizontal: 10,
  },
  scoreDisplay: {
    position: "absolute",
    top: 12,
    right: 16,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    paddingHorizontal: 16,
    paddingVertical: 6,
    borderRadius: 20,
  },
  scoreText: {
    color: "#FFFFFF",
    fontSize: 18,
    fontWeight: "bold",
    fontVariant: ["tabular-nums"],
  },
  pauseOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(0, 0, 0, 0.6)",
    justifyContent: "center",
    alignItems: "center",
  },
  pauseText: {
    color: "#FFFFFF",
    fontSize: 20,
    fontWeight: "bold",
    marginTop: 8,
  },
  tapInstruction: {
    position: "absolute",
    bottom: GROUND_HEIGHT + 8,
    left: 0,
    right: 0,
    alignItems: "center",
  },
  tapText: {
    color: "rgba(0, 0, 0, 0.5)",
    fontSize: 12,
    fontWeight: "500",
  },
  abilityDisplay: {
    position: "absolute",
    top: 12,
    left: 16,
  },
  livesContainer: {
    flexDirection: "row",
    gap: 4,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
  },
  jumpsContainer: {
    flexDirection: "row",
    alignItems: "center",
    gap: 4,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    paddingHorizontal: 10,
    paddingVertical: 6,
    borderRadius: 20,
  },
  jumpsText: {
    color: "#FFFFFF",
    fontSize: 14,
    fontWeight: "600",
  },
  hitFlashOverlay: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: "rgba(239, 68, 68, 0.3)",
  },
  specialJumpButton: {
    position: "absolute",
    bottom: GROUND_HEIGHT + 16,
    right: 16,
    width: 60,
    height: 60,
    borderRadius: 30,
    alignItems: "center",
    justifyContent: "center",
    shadowColor: "#000",
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.25,
    shadowRadius: 4,
    elevation: 5,
  },
  specialJumpButtonText: {
    color: "#FFFFFF",
    fontSize: 12,
    fontWeight: "bold",
    marginTop: -4,
  },
});

